import { createMuiTheme } from '@material-ui/core/styles';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import responsiveFontSizes from '@material-ui/core/styles/responsiveFontSizes';
import { red } from '@material-ui/core/colors';
import { APPBAR, FONT } from '../../constants/style';
import metricWeb from './font/metricWeb';

const breakpoints = createBreakpoints({});
const theme = responsiveFontSizes(
  createMuiTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        lg2: 1536,
        xl: 1920,
      },
    },
    props: {
      MuiButton: {
        disableRipple: true,
        disableFocusRipple: true,
        disableElevation: true,
      },
      MuiButtonBase: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      MuiTextField: {
        size: 'small',
        variant: 'outlined'
      },
      MuiSwitch: {
        color: 'primary'
      },
      MuiSelect: {
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          getContentAnchorEl: null
        }
      }
    },
    palette: {
      hover: {
        main: '#eef4f6',
        contrastText: '#0c6460',
      },
      primary: {
        main: FONT.COLOR.PRIMARY.MAIN,
      },
      secondary: {
        main: FONT.COLOR.SECONDARY.MAIN,
        light: FONT.COLOR.SECONDARY.LIGHT,
      },
      error: {
        main: red.A700,
        dark: FONT.COLOR.ERROR.DARK,
      },
      background: {
        default: '#f8f9fb',
      },
      tab: {
        main: FONT.COLOR.TAB.MAIN,
        contrastText: FONT.COLOR.TAB.CONTRAST_TEXT,
      }
    },
    typography: {
      h1: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      h2: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      h3: {
        fontSize: '2.5rem',
        color: FONT.COLOR.TEXT.MAIN,
      },
      h4: {
        color: FONT.COLOR.TEXT.MAIN,
        fontSize: '26px'
      },
      h5: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      h6: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      subtitle1: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      subtitle2: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      body1: {
        color: FONT.COLOR.TEXT.MAIN,
        lineHeight: 1.25,
      },
      body2: {
        color: FONT.COLOR.TEXT.MAIN,
      },
      caption: {
        color: FONT.COLOR.CAPTION.MAIN,
        textTransform: 'uppercase',
      },
      fontFamily: FONT.FAMILY,
    }
  })
);

theme.overrides = {
  ...theme.overrides,
  MuiAppBar: {
    root: {
      height: `${APPBAR.HEIGHT}px`,
      // Display on top of left drawer
      // 1202
      zIndex: theme.zIndex.drawer + 2,
    },
  },
  MuiAutocomplete: {
    noOptions: {
      fontFamily: FONT.FAMILY,
    },
    option: {
      '&[data-focus="true"]': {
        backgroundColor: '#dcdddf',
        color: '#0c6460'
      },
      '&[aria-selected="true"]': {
        backgroundColor: `${theme.palette.hover.main} !important`,
        color: '#0c6460 !important'
      },
    }
  },
  MuiInputBase: {
    root: {
      borderRadius: '0 !important',
      fontSize: theme.spacing(2),
    }
  },
  MuiSelect: {
    outlined: {
      padding: '10.5px 14px'
    },
  },
  MuiMenu: {
    paper: {
      borderRadius: 0,
      maxHeight: '200px',
      marginTop: '4px',
      '& .MuiList-padding': {
        padding: 0
      },
      '&::-webkit-scrollbar': {
        '-webkit-appearance': 'none',
        background: 'white',
        '&:horizontal': {
          height: '13px',
        },
        '&:vertical': {
          width: '13px',
        }
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '8px',
        border: '2px solid white',
        backgroundColor: '#bababa',
      }
    }
  },
  MuiFormHelperText: {
    root: {
      fontSize: '16px',
      textTransform: 'capitalize',
    },
    contained: {
      margin: 0,
      marginLeft: 0,
      marginRight: 0,
    }
  },
  MuiMenuItem:{
    root: {
      '&:hover': {
        backgroundColor: '#dcdddf',
        color: '#0c6460'
      }
    },
    selected: {
      backgroundColor: `${theme.palette.hover.main} !important`,
      color: '#0c6460 !important'
    }
  },
  MuiCssBaseline: {
    '@global': {
      '@font-face': [
        ...Object.keys(metricWeb).map(key => metricWeb[key]),
      ],
      body: {
        '& a': {
          textDecoration: 'none',
          color: theme.palette.primary.main,
          '&:hover': {
            textDecoration: 'underline',
          }
        }
      }
    },
  },
  MuiIconButton: {
    root: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiCheckbox: {
    root: {
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
    },
  },
  MuiInputLabel: {
    shrink: {
      fontSize: '18px'
    },
  },
  MuiListItem: {
    button: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiTooltip: {
    tooltip: {
      fontSize: 12,
    }
  },
  MuiTabs: {
    root: {
      backgroundColor: FONT.COLOR.SECONDARY.MAIN,
      boxShadow: 'none',
    },
    indicator: {
      backgroundColor: '#ffffff',
    },
    scrollButtons: {
      color: '#ffffff',
    }
  },
  MuiToggleButton: {
    root: {
      '&:hover': {
        color: theme.palette.secondary.contrastText,
        backgroundColor: theme.palette.secondary.light,
      },
      '&$selected': {
        color: theme.palette.secondary.contrastText,
        backgroundColor: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
      }
    }
  },
  MuiFormControl: {
    root: {
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderWidth: '1px',
        },
      },
    }
  },
  MuiToolbar: {
    regular: {
      [theme.breakpoints.up('xs')]: {
        minHeight: `${APPBAR.HEIGHT}px`,
      }
    },
    gutters: {
      paddingLeft: 24,
      paddingRight: 24,
      [breakpoints.down('xs')]: {
        paddingLeft: 16,
        paddingRight: 16,
      },
    }
  }
};


export default theme;